import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Music, Search, Check } from "lucide-react";
import { format } from "date-fns";
import { ru } from "date-fns/locale";

interface Generation {
  id: string;
  story: string;
  status: string;
  demo_url: string | null;
  demo_url_2: string | null;
  full_music_url: string | null;
  full_music_url_2: string | null;
  music_url: string | null;
  created_at: string;
  user_id: string;
  is_full_purchased: boolean | null;
  events: { name: string; emoji: string } | null;
  genres: { name: string; emoji: string } | null;
  telegram_users: { username: string | null; first_name: string | null; telegram_id: number } | null;
}

interface GenerationPickerProps {
  onSelect: (gen: Generation, audioUrl: string) => void;
  selectedId?: string;
}

export function GenerationPicker({ onSelect, selectedId }: GenerationPickerProps) {
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState(false);

  const { data: generations = [], isLoading } = useQuery({
    queryKey: ["admin-generations-picker"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("generations")
        .select("*, events(*), genres(*), telegram_users(*)")
        .eq("status", "completed")
        .order("created_at", { ascending: false })
        .limit(200);
      if (error) throw error;
      return data as unknown as Generation[];
    },
  });

  const filtered = generations.filter((g) => {
    if (!search) return true;
    const q = search.toLowerCase();
    const userName = g.telegram_users?.username || g.telegram_users?.first_name || "";
    return (
      g.story.toLowerCase().includes(q) ||
      userName.toLowerCase().includes(q) ||
      g.events?.name.toLowerCase().includes(q) ||
      g.genres?.name.toLowerCase().includes(q)
    );
  });

  const getAudioUrls = (g: Generation) => {
    const urls: { label: string; url: string }[] = [];
    if (g.full_music_url) urls.push({ label: "Полная 1", url: g.full_music_url });
    if (g.full_music_url_2) urls.push({ label: "Полная 2", url: g.full_music_url_2 });
    if (g.demo_url) urls.push({ label: "Демо 1", url: g.demo_url });
    if (g.demo_url_2) urls.push({ label: "Демо 2", url: g.demo_url_2 });
    return urls;
  };

  if (!expanded) {
    return (
      <Button
        type="button"
        variant="outline"
        className="w-full justify-start gap-2"
        onClick={() => setExpanded(true)}
      >
        <Music className="w-4 h-4" />
        {selectedId ? "✅ Трек выбран — выбрать другой" : "Выбрать трек из генераций"}
      </Button>
    );
  }

  return (
    <div className="border rounded-lg p-3 space-y-3 bg-muted/30">
      <div className="flex items-center gap-2">
        <Search className="w-4 h-4 text-muted-foreground" />
        <Input
          placeholder="Поиск по истории, имени, событию..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="h-8 text-sm"
        />
        <Button variant="ghost" size="sm" onClick={() => setExpanded(false)}>✕</Button>
      </div>

      <div className="max-h-[280px] overflow-y-auto space-y-2">
        {isLoading ? (
          <p className="text-sm text-muted-foreground text-center py-4">Загрузка...</p>
        ) : filtered.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-4">Ничего не найдено</p>
        ) : (
          filtered.map((g) => {
            const audioUrls = getAudioUrls(g);
            const userName = g.telegram_users?.username
              ? `@${g.telegram_users.username}`
              : g.telegram_users?.first_name || "—";
            const isSelected = g.id === selectedId;

            return (
              <div
                key={g.id}
                className={`rounded-lg border p-3 space-y-2 transition-colors ${
                  isSelected ? "border-primary bg-primary/5" : "border-border/50 hover:border-border"
                }`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium truncate">{userName}</p>
                    <p className="text-xs text-muted-foreground line-clamp-1">{g.story}</p>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    {g.events && <Badge variant="outline" className="text-[10px] px-1.5">{g.events.emoji} {g.events.name}</Badge>}
                    {g.is_full_purchased && <Badge className="bg-green-500/10 text-green-500 text-[10px] px-1.5">Куплен</Badge>}
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <p className="text-[10px] text-muted-foreground">
                    {format(new Date(g.created_at), "d MMM yyyy", { locale: ru })}
                    {g.genres && ` · ${g.genres.emoji} ${g.genres.name}`}
                  </p>
                </div>

                {audioUrls.length > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    {audioUrls.map((a) => (
                      <Button
                        key={a.url}
                        type="button"
                        variant={isSelected ? "default" : "outline"}
                        size="sm"
                        className="h-7 text-xs gap-1"
                        onClick={() => {
                          onSelect(g, a.url);
                          setExpanded(false);
                        }}
                      >
                        {isSelected ? <Check className="w-3 h-3" /> : <Music className="w-3 h-3" />}
                        {a.label}
                      </Button>
                    ))}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
